/**
 * CyclesTab Component
 * OKR Cycle management within Admin page
 */

import React, { useState } from 'react';
import { Plus, Calendar, Play, CheckCircle, Clock, Edit, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'react-hot-toast';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, Button, Input, Modal, LoadingSpinner, EmptyState, Badge } from '../../../components/common';
import apiClient from '../../../services/api';
import { usePermissions } from '../../../hooks';
import { CloseCycleModal } from './cycles/CloseCycleModal';
import { ReopenCycleModal } from './cycles/ReopenCycleModal';
import { cycleCardLabels } from './cycles/cycleCardLabels';

interface OKRCycle {
  id: string;
  name: string;
  description?: string;
  type: string;
  startDate: string;
  endDate: string;
  status: 'upcoming' | 'active' | 'closed';
  openOkrs?: number;
  closedAt?: string | null;
  reopenedAt?: string | null;
  reopenableUntil?: string | null;
  closedBy?: { firstName: string; lastName: string } | null;
  reopenedBy?: { firstName: string; lastName: string } | null;
  _count?: {
    okrs: number;
  };
}

export const CyclesTab: React.FC = () => {
  const queryClient = useQueryClient();
  const { can } = usePermissions();
  // DELETE /cycles/:id requires settings:delete, which account_admin does not hold by
  // default. Hide the bin rather than let admins hit a 403 (Task 3, 2026-09-30).
  const canDeleteCycle = can('settings', 'delete');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCycle, setEditingCycle] = useState<OKRCycle | null>(null);
  const [closing, setClosing] = useState<OKRCycle | null>(null);
  const [reopening, setReopening] = useState<OKRCycle | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'quarterly',
    startDate: '',
    endDate: '',
  });

  // Fetch cycles
  const { data: cycles, isLoading } = useQuery({
    queryKey: ['okr-cycles'],
    queryFn: async () => {
      try {
        const response = await apiClient.get<{ success: boolean; data: { cycles: OKRCycle[] } }>('/cycles');
        if (response.data.success) {
          return response.data.data.cycles;
        }
        return [];
      } catch {
        // Cycles API not available yet
        return [];
      }
    },
  });

  // Create cycle mutation
  const createCycle = useMutation({
    mutationFn: async (data: typeof formData) => {
      const response = await apiClient.post('/cycles', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['okr-cycles'] });
      toast.success('Cycle created successfully');
      closeModal();
    },
    onError: () => {
      toast.error('Failed to create cycle');
    },
  });

  // Update cycle mutation
  const updateCycle = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: typeof formData }) => {
      const response = await apiClient.patch(`/cycles/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['okr-cycles'] });
      toast.success('Cycle updated successfully');
      closeModal();
    },
    onError: () => {
      toast.error('Failed to update cycle');
    },
  });

  // Delete cycle mutation
  const deleteCycle = useMutation({
    mutationFn: async (id: string) => {
      const response = await apiClient.delete(`/cycles/${id}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['okr-cycles'] });
      toast.success('Cycle deleted successfully');
    },
    onError: () => {
      toast.error('Failed to delete cycle');
    },
  });

  // Both close and reopen change which OKRs are open/editable and their status,
  // so both need the same invalidation set: the cycles list itself, every place
  // that reads OKR status (lists, detail, org stats), the filter metadata that
  // drives status filter options, and the reopen preview — otherwise, under the
  // app's 5-minute staleTime, ReopenCycleModal can show a stale OKR count left
  // over from before this close/reopen.
  const invalidateAfterCycleChange = () => {
    queryClient.invalidateQueries({ queryKey: ['okr-cycles'] });
    queryClient.invalidateQueries({ queryKey: ['okrs'] });
    queryClient.invalidateQueries({ queryKey: ['okr'] });
    queryClient.invalidateQueries({ queryKey: ['org-stats'] });
    queryClient.invalidateQueries({ queryKey: ['okr-filter-metadata'] });
    queryClient.invalidateQueries({ queryKey: ['cycle-reopen-preview'] });
  };

  // Close cycle mutation
  const closeCycle = useMutation({
    mutationFn: async (id: string) => {
      const response = await apiClient.post(`/cycles/${id}/close`);
      return response.data;
    },
    onSuccess: () => {
      invalidateAfterCycleChange();
      toast.success('Cycle closed successfully');
    },
    onError: () => {
      toast.error('Failed to close cycle');
    },
  });

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingCycle(null);
    setFormData({
      name: '',
      description: '',
      type: 'quarterly',
      startDate: '',
      endDate: '',
    });
  };

  const openCreateModal = () => {
    setEditingCycle(null);
    setFormData({
      name: '',
      description: '',
      type: 'quarterly',
      startDate: '',
      endDate: '',
    });
    setIsModalOpen(true);
  };

  const openEditModal = (cycle: OKRCycle) => {
    setEditingCycle(cycle);
    setFormData({
      name: cycle.name,
      description: cycle.description || '',
      type: cycle.type,
      startDate: cycle.startDate.split('T')[0],
      endDate: cycle.endDate.split('T')[0],
    });
    setIsModalOpen(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.startDate || !formData.endDate) return;

    if (editingCycle) {
      updateCycle.mutate({ id: editingCycle.id, data: formData });
    } else {
      createCycle.mutate(formData);
    }
  };

  const handleDelete = (cycle: OKRCycle) => {
    if (cycle.status === 'active') {
      toast.error('Cannot delete an active cycle');
      return;
    }
    if (window.confirm(`Are you sure you want to delete "${cycle.name}"?`)) {
      deleteCycle.mutate(cycle.id);
    }
  };

  const handleCloseCycle = (cycle: OKRCycle) => {
    setClosing(cycle);
  };

  const handleReopenDone = () => {
    setReopening(null);
    invalidateAfterCycleChange();
    toast.success('Cycle reopened');
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <Play className="w-4 h-4" />;
      case 'upcoming':
        return <Clock className="w-4 h-4" />;
      case 'closed':
        return <CheckCircle className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  const getStatusBadgeVariant = (status: string): 'success' | 'warning' | 'secondary' => {
    switch (status) {
      case 'active':
        return 'success';
      case 'upcoming':
        return 'warning';
      case 'closed':
        return 'secondary';
      default:
        return 'secondary';
    }
  };

  const getCycleTypeLabel = (type: string) => {
    switch (type) {
      case 'quarterly':
        return 'Quarterly';
      case 'monthly':
        return 'Monthly';
      case 'annual':
        return 'Annual';
      case 'custom':
        return 'Custom';
      default:
        return type;
    }
  };

  // Group cycles by status
  const activeCycles = cycles?.filter((c) => c.status === 'active') || [];
  const upcomingCycles = cycles?.filter((c) => c.status === 'upcoming') || [];
  const closedCycles = cycles?.filter((c) => c.status === 'closed') || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-stone-900" style={{ fontFamily: 'Fraunces, Georgia, serif' }}>OKR Cycles</h2>
          <p className="text-sm text-stone-500">
            Manage planning periods for your organization's OKRs
          </p>
        </div>
        <Button variant="primary" onClick={openCreateModal}>
          <Plus className="w-4 h-4 mr-2" />
          Create Cycle
        </Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      ) : !cycles?.length ? (
        <EmptyState
          icon={Calendar}
          title="No cycles yet"
          description="Create your first OKR cycle to start planning"
          action={{
            label: 'Create Cycle',
            onClick: openCreateModal,
          }}
        />
      ) : (
        <div className="space-y-8">
          {/* Active Cycles */}
          {activeCycles.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-stone-500 uppercase tracking-wider mb-4">
                Active Cycles
              </h3>
              <div className="space-y-4">
                {activeCycles.map((cycle) => (
                  <CycleCard
                    key={cycle.id}
                    cycle={cycle}
                    onEdit={() => openEditModal(cycle)}
                    onDelete={canDeleteCycle ? () => handleDelete(cycle) : undefined}
                    onClose={() => handleCloseCycle(cycle)}
                    getStatusIcon={getStatusIcon}
                    getStatusBadgeVariant={getStatusBadgeVariant}
                    getCycleTypeLabel={getCycleTypeLabel}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Upcoming Cycles */}
          {upcomingCycles.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-stone-500 uppercase tracking-wider mb-4">
                Upcoming Cycles
              </h3>
              <div className="space-y-4">
                {upcomingCycles.map((cycle) => (
                  <CycleCard
                    key={cycle.id}
                    cycle={cycle}
                    onEdit={() => openEditModal(cycle)}
                    onDelete={canDeleteCycle ? () => handleDelete(cycle) : undefined}
                    onClose={() => handleCloseCycle(cycle)}
                    getStatusIcon={getStatusIcon}
                    getStatusBadgeVariant={getStatusBadgeVariant}
                    getCycleTypeLabel={getCycleTypeLabel}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Closed Cycles */}
          {closedCycles.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-stone-500 uppercase tracking-wider mb-4">
                Closed Cycles
              </h3>
              <div className="space-y-4">
                {closedCycles.map((cycle) => (
                  <CycleCard
                    key={cycle.id}
                    cycle={cycle}
                    onDelete={canDeleteCycle ? () => handleDelete(cycle) : undefined}
                    onReopen={() => setReopening(cycle)}
                    getStatusIcon={getStatusIcon}
                    getStatusBadgeVariant={getStatusBadgeVariant}
                    getCycleTypeLabel={getCycleTypeLabel}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Create/Edit Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={closeModal}
        title={editingCycle ? 'Edit Cycle' : 'Create Cycle'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Cycle Name *
            </label>
            <Input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Q1 2025"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Description
            </label>
            <Input
              type="text"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Optional description"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Cycle Type
            </label>
            <select
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value })}
              className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="quarterly">Quarterly</option>
              <option value="monthly">Monthly</option>
              <option value="annual">Annual</option>
              <option value="custom">Custom</option>
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">
                Start Date *
              </label>
              <Input
                type="date"
                value={formData.startDate}
                onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">
                End Date *
              </label>
              <Input
                type="date"
                value={formData.endDate}
                onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="secondary" onClick={closeModal}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={!formData.name || !formData.startDate || !formData.endDate || createCycle.isPending || updateCycle.isPending}
            >
              {createCycle.isPending || updateCycle.isPending ? (
                <LoadingSpinner size="sm" />
              ) : editingCycle ? (
                'Update'
              ) : (
                'Create'
              )}
            </Button>
          </div>
        </form>
      </Modal>

      <CloseCycleModal
        cycle={closing && { id: closing.id, name: closing.name, openOkrs: closing.openOkrs ?? 0 }}
        isClosing={closeCycle.isPending}
        onConfirm={() => closeCycle.mutate(closing!.id, { onSettled: () => setClosing(null) })}
        onCancel={() => setClosing(null)}
      />

      <ReopenCycleModal
        cycle={reopening && { id: reopening.id, name: reopening.name }}
        onDone={handleReopenDone}
        onCancel={() => setReopening(null)}
      />
    </div>
  );
};

// Cycle Card Component
interface CycleCardProps {
  cycle: OKRCycle;
  onEdit?: () => void;
  onDelete?: () => void;
  onClose?: () => void;
  onReopen?: () => void;
  getStatusIcon: (status: string) => React.ReactNode;
  getStatusBadgeVariant: (status: string) => 'success' | 'warning' | 'secondary';
  getCycleTypeLabel: (type: string) => string;
}

const CycleCard: React.FC<CycleCardProps> = ({
  cycle,
  onEdit,
  onDelete,
  onClose,
  onReopen,
  getStatusIcon,
  getStatusBadgeVariant,
  getCycleTypeLabel,
}) => {
  const labels = cycleCardLabels(cycle, new Date());
  return (
    <Card className="p-4">
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
            <Calendar className="w-6 h-6 text-primary-600" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h4 className="font-medium text-stone-900">{cycle.name}</h4>
              <Badge variant={getStatusBadgeVariant(cycle.status)}>
                <span className="flex items-center gap-1">
                  {getStatusIcon(cycle.status)}
                  {cycle.status}
                </span>
              </Badge>
              <Badge variant="secondary">{getCycleTypeLabel(cycle.type)}</Badge>
            </div>
            {cycle.description && (
              <p className="text-sm text-stone-500 mt-1">{cycle.description}</p>
            )}
            <div className="flex items-center gap-4 mt-2 text-sm text-stone-500">
              <span>
                {format(new Date(cycle.startDate), 'MMM d, yyyy')} - {format(new Date(cycle.endDate), 'MMM d, yyyy')}
              </span>
              {cycle._count?.okrs !== undefined && (
                <span>{cycle._count.okrs} OKRs</span>
              )}
            </div>
            {(labels.closedLine || labels.reopenLine || labels.reopenedLine) && (
              <div className="mt-1 text-sm text-stone-500 space-x-3">
                {labels.closedLine && <span>{labels.closedLine}</span>}
                {labels.reopenLine && <span>{labels.reopenLine}</span>}
                {labels.reopenedLine && <span>{labels.reopenedLine}</span>}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {cycle.status !== 'closed' && onClose && (
            <Button variant="secondary" size="sm" onClick={onClose}>
              Close Cycle
            </Button>
          )}
          {cycle.status === 'closed' && labels.canReopen && onReopen && (
            <Button variant="secondary" size="sm" onClick={onReopen}>
              Reopen
            </Button>
          )}
          {onEdit && cycle.status !== 'closed' && (
            <button
              onClick={onEdit}
              className="p-2 text-stone-400 hover:text-primary-600 rounded-lg hover:bg-stone-100"
            >
              <Edit className="w-4 h-4" />
            </button>
          )}
          {onDelete && cycle.status !== 'active' && (
            <button
              onClick={onDelete}
              className="p-2 text-stone-400 hover:text-danger-600 rounded-lg hover:bg-stone-100"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </Card>
  );
};
